import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import FeaturedImage from '@/components/FeaturedImage';
import GalleryStrip from '@/components/GalleryStrip';
import { api } from '@/lib/api';
import { useSite } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errorMessage';

const WEDDING_SERVICES = [
    'Bouquet sposa',
    'Boutonnière e corsage',
    'Allestimento cerimonia',
    'Decorazione location',
    'Centrotavola',
    'Tableau e dettagli floreali',
    'Flower design personalizzato',
];

const METHOD_STEPS = [
    { num: '01', title: 'Incontro conoscitivo', desc: 'Ci raccontate il vostro progetto, lo stile, le emozioni. Ascoltiamo prima di proporre.' },
    { num: '02', title: 'Definizione dello stile', desc: 'Insieme scegliamo palette, fiori di stagione, atmosfere. Vi guidiamo nei dettagli.' },
    { num: '03', title: 'Preventivo personalizzato', desc: 'Vi presentiamo il vostro preventivo e lo affiniamo fino a che non è perfetto per voi.' },
    { num: '04', title: 'Allestimento e coordinamento', desc: 'Il giorno del sì, ci occupiamo di tutto: voi pensate solo a vivere il momento.' },
];

function MatrimoniForm() {
    const [form, setForm] = useState({
        full_name: '', lastname: '', email: '', phone: '', event_date: '',
        location_ceremony: '', location_reception: '',
        budget: '', style: '', notes: '', privacy: false,
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const validate = () => {
        const e = {};
        if (!form.full_name.trim()) e.full_name = 'Necessario';
        if (!form.lastname.trim()) e.lastname = 'Necessario';
        // Email e telefono ora entrambi obbligatori
        if (!form.email.trim()) e.email = 'Necessario';
        else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Email non valida';
        if (!form.phone.trim()) e.phone = 'Necessario';
        else if (!/^[+\d\s\-()]{6,}$/.test(form.phone.trim())) e.phone = 'Numero non valido';
        if (!form.event_date) e.event_date = 'Necessario';
        // location_ceremony e location_reception ora facoltativi
        if (!form.privacy) e.privacy = 'Necessario per inviare la richiesta';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev?.preventDefault?.();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await api.post('/matrimoni-requests', {
                full_name: `${form.full_name.trim()} ${form.lastname.trim()}`,
                email: form.email || null,
                phone: form.phone || null,
                event_date: form.event_date,
                location_ceremony: form.location_ceremony,
                location_reception: form.location_reception,
                budget: form.budget || null,
                style: form.style || null,
                notes: form.notes || null,
                privacy_consent: form.privacy,
            });
            toast.success('Richiesta inviata.', { description: 'Ti ricontatteremo per approfondire il progetto.' });
            setSuccess(true);
        } catch (err) {
            toast.error(errorMessage(err, 'Qualcosa non ha funzionato. Riprova tra poco.'));
        } finally { setSubmitting(false); }
    };

    if (success) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="rounded-xl bg-atelier-sage/15 border border-atelier-sage/30 p-8 text-center"
            >
                <CheckCircle2 className="h-10 w-10 mx-auto text-atelier-sage-ink" />
                <h3 className="mt-4 font-serif text-2xl sm:text-3xl">Grazie, abbiamo ricevuto la tua richiesta.</h3>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                    Ti ricontatteremo per approfondire il progetto floreale del tuo matrimonio.
                </p>
            </motion.div>
        );
    }

    return (
        <form data-testid="matrimoni-form" onSubmit={onSubmit} className="rounded-xl border border-border bg-card p-6 sm:p-8 space-y-4">
            <div>
                <Label htmlFor="m-name" className="eyebrow">Nome *</Label>
                <Input id="m-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={cn('mt-1.5', errors.full_name && 'border-destructive')} />
                {errors.full_name && <p className="text-xs text-destructive mt-1">{errors.full_name}</p>}
            </div>
            <div>
                <Label htmlFor="m-lastname" className="eyebrow">Cognome *</Label>
                <Input id="m-lastname" data-testid="matrimoni-form-lastname" value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })} className={cn('mt-1.5', errors.lastname && 'border-destructive')} />
                {errors.lastname && <p className="text-xs text-destructive mt-1">{errors.lastname}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="m-email" className="eyebrow">Email *</Label>
                    <Input id="m-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cn('mt-1.5', errors.email && 'border-destructive')} />
                    {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                </div>
                <div>
                    <Label htmlFor="m-phone" className="eyebrow">Telefono *</Label>
                    <Input id="m-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={cn('mt-1.5', errors.phone && 'border-destructive')} placeholder="Es. 339 123 4567" />
                    {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
                </div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Indica almeno uno tra email e telefono.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="m-date" className="eyebrow">Data del matrimonio *</Label>
                    <Input id="m-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className={cn('mt-1.5', errors.event_date && 'border-destructive')} />
                    {errors.event_date && <p className="text-xs text-destructive mt-1">{errors.event_date}</p>}
                </div>
                <div>
                    <Label htmlFor="m-budget" className="eyebrow">Budget indicativo</Label>
                    <Input id="m-budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1.5" placeholder="Es. 2.000 – 4.000 €" />
                </div>
            </div>
            <div>
                <Label htmlFor="m-ceremony" className="eyebrow">Location cerimonia</Label>
                <Input id="m-ceremony" value={form.location_ceremony} onChange={(e) => setForm({ ...form, location_ceremony: e.target.value })} className={cn('mt-1.5', errors.location_ceremony && 'border-destructive')} placeholder="Città e nome chiesa/civile" />
                {errors.location_ceremony && <p className="text-xs text-destructive mt-1">{errors.location_ceremony}</p>}
            </div>
            <div>
                <Label htmlFor="m-reception" className="eyebrow">Location ricevimento *</Label>
                <Input id="m-reception" value={form.location_reception} onChange={(e) => setForm({ ...form, location_reception: e.target.value })} className={cn('mt-1.5', errors.location_reception && 'border-destructive')} placeholder="Nome della location" />
                {errors.location_reception && <p className="text-xs text-destructive mt-1">{errors.location_reception}</p>}
            </div>
            <div>
                <Label htmlFor="m-style" className="eyebrow">Stile desiderato</Label>
                <Input id="m-style" value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} className="mt-1.5" placeholder="Es. romantico, country, minimal, classico..." />
            </div>
            <div>
                <Label htmlFor="m-notes" className="eyebrow">Note</Label>
                <Textarea id="m-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Raccontaci il vostro progetto, le ispirazioni, i fiori preferiti..." />
            </div>
            <label className="flex items-start gap-3 cursor-pointer pt-1">
                <Checkbox
                    checked={form.privacy}
                    onCheckedChange={(v) => setForm({ ...form, privacy: !!v })}
                    className="mt-0.5"
                />
                <span className="text-sm text-muted-foreground leading-relaxed">
                    Acconsento al trattamento dei miei dati personali per essere ricontattato/a sul progetto del mio matrimonio, come descritto nell'<Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">informativa privacy</Link>. *
                </span>
            </label>
            {errors.privacy && <p className="text-xs text-destructive -mt-2">{errors.privacy}</p>}
            <Button type="submit" disabled={submitting || !form.privacy} className="h-12 w-full sm:w-auto px-6">
                {submitting ? 'Un momento…' : (<><Send className="h-4 w-4 mr-2" />Invia richiesta</>)}
            </Button>
        </form>
    );
}

export default function Matrimoni() {
    const { t } = useSite();
    useSeo({
        title: `${SITE_NAME} — Matrimoni`,
        description: "Allestimenti floreali per matrimoni a Lomazzo e nel Comasco: bouquet sposa, cerimonia, ricevimento, centrotavola. Consulenza dedicata.",
    });
    return (
        <div data-testid={TEST_IDS.pageMatrimoni}>
            {/* Hero */}
            <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-20 overflow-hidden">
                <div className="container-atelier grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <p className="eyebrow mb-4">{t('matrimoni.hero.eyebrow', 'Matrimoni')}</p>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            {t('matrimoni.hero.title', 'Fiori che raccontano la vostra storia.')}
                        </h1>
                        <p className="mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-prose">
                            {t('matrimoni.hero.description', 'Ogni matrimonio è unico e merita un progetto floreale pensato nei minimi dettagli.')}
                        </p>
                        <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-prose">
                            {t('matrimoni.hero.description2', 'Accompagniamo gli sposi nella scelta di colori, materiali e atmosfere per creare allestimenti che rispecchino la loro personalità e valorizzino ogni momento della giornata.')}
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <FeaturedImage
                            slot="hero_matrimoni"
                            alt="Allestimento floreale per matrimonio Floreal"
                            priority
                            className="rounded-xl aspect-[4/5] w-full shadow-soft"
                            fallbackLabel="Matrimoni"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Servizi — solo titolo, no lead */}
            <Section title={t('matrimoni.services.title', 'Tutto ciò che riguarda i fiori, in un unico atelier.')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {WEDDING_SERVICES.map((s, i) => (
                        <motion.div
                            key={s}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                            className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4"
                        >
                            <span className="h-7 w-7 rounded-full bg-atelier-sage/20 flex items-center justify-center shrink-0">
                                <Check className="h-3.5 w-3.5 text-atelier-sage-ink" strokeWidth={2.5} />
                            </span>
                            <span className="text-sm sm:text-[15px]">{s}</span>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Method */}
            <Section className="py-12 sm:py-16 lg:py-20" title={t('matrimoni.method.title', 'Quattro tappe, una storia.')}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                    {METHOD_STEPS.map((s, i) => (
                        <motion.article
                            key={s.num}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.4, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                            className="rounded-xl border border-border bg-card p-6 sm:p-7"
                        >
                            <p className="font-serif text-atelier-bronze text-2xl mb-3">{s.num}</p>
                            <h3 className="font-serif text-xl sm:text-2xl tracking-tight">{s.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                        </motion.article>
                    ))}
                </div>
            </Section>

            {/* Photo strip — solo categoria matrimoni */}
            <Section className="py-8 sm:py-12">
                <GalleryStrip
                    category="matrimoni"
                    limit={4}
                    tileClass="rounded-lg aspect-[4/5]"
                    ariaLabel="Matrimoni realizzati da Floreal"
                />
            </Section>

            {/* CTA — "Lasciati ispirare" / "Raccontaci il progetto" */}
            <Section className="py-12 sm:py-16">
                <div className="rounded-xl bg-atelier-ivory border border-atelier-sage/25 px-6 sm:px-10 py-12 sm:py-16 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl mx-auto leading-[1.1]">
                        {t('matrimoni.cta.title', 'Pronti a iniziare?')}
                    </h3>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button asChild size="lg" variant="outline" className="h-12 px-6 border-foreground/20 bg-background">
                            <Link to="/galleria?cat=matrimoni">{t('matrimoni.cta.btn_inspire', 'Lasciati ispirare')} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                        </Button>
                    </div>
                </div>
            </Section>

            {/* Form Matrimoni */}
            <Section id="raccontaci" className="py-12 sm:py-16 lg:py-20" title={t('matrimoni.form.title', 'Raccontaci il vostro matrimonio')}>
                <div className="max-w-3xl mx-auto">
                    <MatrimoniForm />
                </div>
            </Section>
        </div>
    );
}
